// THE PERSON RASTER, VERSION 1 — five blocks, 26 comparable cells for the people on the record
// (BUILD-BRIEF-R7 §1). It lives in `src/shared/` for the same reason `raster.v1.ts` does: the
// server parses, computes and scores against it, the console renders its labels, and the two
// must never carry a second spelling of anything.
//
// It is written in the organisation raster's own DSL and RE-USES ITS PARSERS (§1: "delegates to
// `parseCell`") — a person cell is an enum, a band, a count, a year or a reference exactly as an
// organisation cell is, and `parseCell` stays the one place in the codebase that turns text into
// a typed value. What this module adds is what a person needs and an organisation does not:
//
//   · a KIND per cell (§0.2) — ESTIMATE, LOOK UP, SOURCE OR LEAVE, COMPUTED, DECLARED — which
//     says how a value may come to be. A name, a number, a date or a year is never estimated; a
//     computed cell is never proposed and never in `facts`; a declared cell is the operator's
//     own hand and never an agent's;
//   · `TITLE_MAP` (§1.6) — the reviewed dictionary that turns 1'422 verbatim role titles in
//     dozens of spellings into a function family and a seniority rank, and the compound rule;
//   · `buyingInfluenceFor` (§1.2) — the pure table that reads a person's place in a purchase off
//     their rank, their function and, when the register says so, their signature;
//   · `EXCLUDED_PERSON_TOPICS` (§5.5) — the LPD art. 5 let. c vocabulary in FR and DE that a
//     person fact may never carry, so the refusal is drawn in code and not in a prompt.
//
// Facts are keyed `person.<block>.<cell>` with `subject_kind = 'person'`; computed values live in
// `persons_computed` and carry no claim mark (P12). A cell nobody filled reads "not collected",
// never zero — except where §1.5 names a counted state (`never`, a `0` once looked up).

import {
  bandFor, findCell, labelFor, parseCell,
  type RasterBand, type RasterCell, type RasterCellType, type RasterLang, type RasterParseError,
  type RasterParseResult, type RasterValue,
} from './raster.v1.js';
import { normalisePhone } from './contact.js';
import type { PersonCellState, PersonProfileCellKind, PersonProfileCellType, PersonProfileCoverage } from './types.js';

export const PERSON_RASTER_VERSION = 'v1';

// ── the shapes ──────────────────────────────────────────────────────────────

/**
 * HOW A VALUE MAY COME TO BE (§0.2). The kind is a property of the CELL, not of a fact: it says
 * which methods a proposal on this cell may carry, and which rung of the ladder a run may climb.
 *   estimate         a band, a scale or a category the person's held title, employer or mandate
 *                    implies — never left open once its basis is on the record
 *   look_up          a fact a register, a directory, the employer's page or the operator's own
 *                    mail states — found with its quote, a counted absence, or `not_found`
 *   source_or_leave  a name, a number, a date, a year — a verbatim source or nothing
 *   computed         arithmetic over the record — never proposed, never in `facts`, no mark
 *   declared         the operator's own hand — never proposed by an agent
 */
export type PersonCellKind = 'estimate' | 'look_up' | 'source_or_leave' | 'computed' | 'declared';
export const PERSON_CELL_KINDS: readonly PersonCellKind[] = ['estimate', 'look_up', 'source_or_leave', 'computed', 'declared'];

/**
 * APPLICABILITY for a person (§1.3). The three governance cells are asked only of the people they
 * can be true of: a register function needs an employer that is IN the register and a rank that
 * could be inscribed; a signature clause needs a register function; an external mandate is asked
 * of cadres and above. As in `raster.v1.ts`, a cell whose condition cannot be judged (nothing
 * held yet) is ASKED — when in doubt, it applies — and a cell that does not apply reads
 * "not applicable", a state beside filled / not collected / pending / not found.
 */
export interface PersonAppliesWhen {
  /** The current employer has a `register_organisations` row (a UID). */
  has_register_row?: true;
  /** Seniority tokens the cell is asked of; a person whose seniority is not held yet is asked. */
  seniority?: readonly PersonSeniority[];
  /** Asked only once this other cell is held — and, with `not`, held with a value outside the list. */
  cell_held?: { key: string; not?: readonly string[] };
}

/** What the caller knows about THIS person when judging applicability. `undefined` = unknown
 *  (asked); `null` = looked and nothing held (for `register_function`: not applicable). */
export interface PersonApplicabilityContext {
  has_register_row?: boolean | null;
  seniority?: string | null;
  register_function?: string | null;
}

export interface PersonRasterCell extends Omit<RasterCell, 'fit_relevant' | 'applies_when'> {
  kind: PersonCellKind;
  /** The people this cell is asked of; absent = everyone. */
  applies_when?: PersonAppliesWhen;
}

export interface PersonRasterBlock {
  key: string;
  label: string;
  cells: PersonRasterCell[];
  /** Every cell computed nightly (the engagement block): never proposed, no claim mark. */
  computed?: boolean;
}

// ── the declaration DSL (mirrors raster.v1.ts: [value, en, de, fr]) ─────────

type ValueSpec = readonly [value: string, en: string, de: string, fr: string];
type BandSpec = readonly [value: string, en: string, de: string, fr: string, min: number | null, max: number | null];

interface CellSpec {
  key: string;
  en: string;
  de: string;
  fr: string;
  type: RasterCellType;
  kind: PersonCellKind;
  unit?: string;
  values?: readonly ValueSpec[];
  bands?: readonly BandSpec[];
  /** The ladder of §1, copied: where a value comes from, rung by rung, and what is refused. */
  sources_hint: string;
  /** Defaults to `type !== 'text'` — P11: a text cell is never scored or counted. */
  comparable?: boolean;
  applies_when?: PersonAppliesWhen;
}

interface BlockSpec {
  key: string;
  en: string;
  de: string;
  fr: string;
  computed?: boolean;
  cells: readonly CellSpec[];
}

const DE_BLOCKS: Record<string, string> = {};
const FR_BLOCKS: Record<string, string> = {};
const DE_CELLS: Record<string, string> = {};
const FR_CELLS: Record<string, string> = {};
const DE_VALUES: Record<string, string> = {};
const FR_VALUES: Record<string, string> = {};

function buildBlock(spec: BlockSpec): PersonRasterBlock {
  DE_BLOCKS[spec.key] = spec.de;
  FR_BLOCKS[spec.key] = spec.fr;
  const cells = spec.cells.map((c): PersonRasterCell => {
    const path = `${spec.key}.${c.key}`;
    DE_CELLS[path] = c.de;
    FR_CELLS[path] = c.fr;
    const values = c.values?.map(([value, en, de, fr]): RasterValue => {
      DE_VALUES[`${path}.${value}`] = de;
      FR_VALUES[`${path}.${value}`] = fr;
      return { value, label: en };
    });
    const bands = c.bands?.map(([value, en, de, fr, min, max]): RasterBand => {
      DE_VALUES[`${path}.${value}`] = de;
      FR_VALUES[`${path}.${value}`] = fr;
      return { value, label: en, min, max };
    });
    return {
      key: c.key,
      label: c.en,
      type: c.type,
      kind: c.kind,
      ...(c.unit ? { unit: c.unit } : {}),
      ...(values ? { values } : {}),
      ...(bands ? { bands } : {}),
      sources_hint: c.sources_hint,
      comparable: c.comparable ?? c.type !== 'text',
      ...(c.applies_when ? { applies_when: c.applies_when } : {}),
    };
  });
  return { key: spec.key, label: spec.en, cells, ...(spec.computed ? { computed: true as const } : {}) };
}

// ── the vocabularies the blocks share ───────────────────────────────────────

/** The seniority ranks, in band order (§1.2). The synthetic ranks let a filter ask "cadre_superieur and above". */
export const SENIORITIES = ['gouvernance', 'direction', 'direction_adjointe', 'cadre_superieur', 'cadre_intermediaire', 'collaborateur', 'en_formation_benevole'] as const;
export type PersonSeniority = (typeof SENIORITIES)[number];

export const FUNCTION_FAMILIES = [
  'direction_generale', 'gouvernance', 'finances_administration', 'ressources_humaines', 'informatique',
  'qualite_securite_lpd', 'communication_recherche_fonds', 'prestations_socio_educatives', 'therapeutique_sante',
  'enseignement_specialise', 'intendance_exploitation', 'autre',
] as const;
export type PersonFunctionFamily = (typeof FUNCTION_FAMILIES)[number];

export const BUYING_INFLUENCES = ['economic', 'ratifier', 'budget', 'technical', 'user', 'none_known'] as const;
export type PersonBuyingInfluence = (typeof BUYING_INFLUENCES)[number];

export type PersonSignatureMode = 'individuelle' | 'collective_a_deux' | 'sans_signature';

/** The ranks a register can inscribe (§1.3): `register_function` and `external_mandates` are asked of these. */
const INSCRIBABLE: readonly PersonSeniority[] = ['gouvernance', 'direction', 'direction_adjointe', 'cadre_superieur'];

// The 26 cantons are ONE list: `work_canton` reads the organisation raster's `identity.canton`
// vocabulary (values and its DE/FR labels) so a person's canton and their employer's can never
// disagree on a code or a spelling. The list stays declared in `raster.v1.ts`.
const CANTON_CELL = findCell('profile.identity.canton');
if (!CANTON_CELL?.cell.values) throw new Error('person-raster.v1: raster.v1 has no identity.canton vocabulary to share');
const CANTONS: readonly ValueSpec[] = CANTON_CELL.cell.values.map((v): ValueSpec => [
  v.value, v.label, labelFor('de', 'identity', 'canton', v.value), labelFor('fr', 'identity', 'canton', v.value),
]);

// ── the five blocks ─────────────────────────────────────────────────────────

const CONTACT = buildBlock({
  key: 'contact',
  en: 'Contact and place of work',
  de: 'Kontakt und Arbeitsort',
  fr: 'Coordonnées et lieu de travail',
  cells: [
    {
      key: 'email_work', en: 'Work e-mail', de: 'Geschäftliche E-Mail', fr: 'E-mail professionnel',
      type: 'text', kind: 'source_or_leave',
      sources_hint: '(1) the From header of a mail the person sent the operator — import, evidence interaction:<id>; (2) the person’s own signature block — import:mail, quote verified against the stored body → held on arrival; (3) the employer’s team/contact page line naming the person — scout snapshot, read, held on arrival; (4) Surfe professional e-mail (§5.2) — read, evidence the stored raw response, NEVER held on arrival; (5) the operator. Never an address patterned from a colleague’s.',
    },
    {
      key: 'email_kind', en: 'E-mail kind', de: 'Art der E-Mail', fr: 'Type d’e-mail',
      type: 'enum', kind: 'computed',
      sources_hint: 'Nightly from the held email_work and the current employer’s organisations.domains: a name-shaped local part on an employer domain → professional_named; info@/direction@/secretariat@/contact@ on it → professional_generic; a non-employer domain the person themselves wrote from → personal_by_correspondence; no employer domain held → not collected.',
      values: [
        ['professional_named', 'Professional, named', 'Geschäftlich, namentlich', 'Professionnel nominatif'],
        ['professional_generic', 'Professional, generic', 'Geschäftlich, generisch', 'Professionnel générique'],
        ['personal_by_correspondence', 'Personal, by correspondence', 'Privat, aus Korrespondenz', 'Personnel, par correspondance'],
      ],
    },
    {
      key: 'email_status', en: 'E-mail status', de: 'E-Mail-Status', fr: 'Statut de l’e-mail',
      type: 'enum', kind: 'computed',
      sources_hint: 'Nightly from the email_work fact’s method, evidence and as_of, and the operator’s mail events: an inbound mail from the address, or a reply to one → confirmed_by_reply; read on a page or a signature ≤ 12 months ago → read_public; Surfe validationStatus VALID → provider_asserted; typed → declared; a DSN naming the address → bounced (the importer must learn DSNs — a named gap); anything older than 12 months without a mail event → stale_12m.',
      values: [
        ['confirmed_by_reply', 'Confirmed by reply', 'Durch Antwort bestätigt', 'Confirmé par réponse'],
        ['read_public', 'Read from a public source', 'Aus öffentlicher Quelle gelesen', 'Lu sur une source publique'],
        ['provider_asserted', 'Asserted by a provider', 'Von einem Anbieter behauptet', 'Affirmé par un fournisseur'],
        ['declared', 'Declared', 'Erklärt', 'Déclaré'],
        ['bounced', 'Bounced', 'Unzustellbar', 'Rejeté'],
        ['stale_12m', 'Stale (over 12 months)', 'Veraltet (über 12 Monate)', 'Périmé (plus de 12 mois)'],
      ],
    },
    {
      key: 'phone_work', en: 'Work phone', de: 'Geschäftliche Telefonnummer', fr: 'Téléphone professionnel',
      type: 'text', kind: 'source_or_leave',
      sources_hint: 'A number, +41… normalised. (1) the employer’s page line naming the person with a direct line — snapshot, read, held on arrival; (2) their signature — import:mail, held on arrival; (3) the operator. NEVER a Surfe mobile, never a private directory listing, never a number remembered. The organisation’s switchboard is an ORGANISATION fact (contact.switchboard, §5.3).',
    },
    {
      key: 'phone_kind', en: 'Phone kind', de: 'Art der Nummer', fr: 'Type de numéro',
      type: 'enum', kind: 'look_up',
      sources_hint: 'read with phone_work’s quote («ligne directe», a number unique to the person → direct_work; a mobile the person signs with or the employer publishes → mobile_professional); COMPUTED switchboard_only when no personal line is held and the organisation holds a switchboard; none_published read with the two snapshots as evidence when the page and the directory were checked and nothing named the person; nothing checked → not collected.',
      values: [
        ['direct_work', 'Direct line', 'Direktwahl', 'Ligne directe'],
        ['mobile_professional', 'Professional mobile', 'Geschäftliches Mobiltelefon', 'Mobile professionnel'],
        ['switchboard_only', 'Switchboard only', 'Nur Zentrale', 'Central uniquement'],
        ['none_published', 'None published', 'Keine veröffentlicht', 'Aucun publié'],
      ],
    },
    {
      key: 'linkedin_url', en: 'LinkedIn', de: 'LinkedIn', fr: 'LinkedIn',
      type: 'text', kind: 'source_or_leave',
      sources_hint: 'A URL on linkedin.com/in/. (1) the seed’s source_url (import); (2) the Surfe preview’s linkedInUrl at 0 credits — a proposal, evidence the stored response snapshot, reviewed by hand; (3) a signature line — import:mail, held on arrival; (4) an employer page linking the profile — read; (5) the operator’s paste. The profile itself is NEVER fetched, read or quoted (scout/politeness.ts BLOCKED_HOST_SUFFIXES).',
    },
    {
      key: 'work_canton', en: 'Canton of work', de: 'Arbeitskanton', fr: 'Canton de travail',
      type: 'enum', kind: 'look_up', values: CANTONS,
      sources_hint: 'read from the team/site/contact page or the signature’s postal line naming the person’s site; else COMPUTED with no mark from the current held affiliation’s organisation profile.identity.canton when that organisation’s mandate.coverage_geo ≠ multi_canton; a multi-canton employer with no named site → open (a run that finds no site files not_found); no held affiliation → not collected. Surfe location never feeds it.',
    },
    {
      key: 'work_commune', en: 'Commune of work', de: 'Arbeitsgemeinde', fr: 'Commune de travail',
      type: 'text', kind: 'look_up',
      sources_hint: 'The commune as the source spells it: the site page or the signature’s postal line; else COMPUTED from the organisation’s seat only when operating.sites_count = 1; a multi-site organisation with no named site → not collected (never the seat). NOT a domicile. Supersedes the free key identity.location (§3.1); the applier mirrors it into persons.location.',
    },
  ],
});

const JOB = buildBlock({
  key: 'job',
  en: 'Function',
  de: 'Funktion',
  fr: 'Fonction',
  cells: [
    {
      key: 'function_family', en: 'Function family', de: 'Funktionsfamilie', fr: 'Famille de fonction',
      type: 'enum', kind: 'estimate',
      sources_hint: 'COMPUTED with no mark by TITLE_MAP from the current held affiliation’s verbatim role_title when an entry matches (the title is always printed beside the token); else an agent files estimated 0.5–0.7 from the team page or organigramme with the line as quote; a title on record that matches nothing → autre COMPUTED, never open. No current affiliation → not collected.',
      values: [
        ['direction_generale', 'General management', 'Geschäftsleitung', 'Direction générale'],
        ['gouvernance', 'Governance', 'Gremium', 'Gouvernance'],
        ['finances_administration', 'Finance and administration', 'Finanzen und Administration', 'Finances et administration'],
        ['ressources_humaines', 'Human resources', 'Personal', 'Ressources humaines'],
        ['informatique', 'IT', 'Informatik', 'Informatique'],
        ['qualite_securite_lpd', 'Quality, safety and data protection', 'Qualität, Sicherheit und Datenschutz', 'Qualité, sécurité et LPD'],
        ['communication_recherche_fonds', 'Communication and fundraising', 'Kommunikation und Fundraising', 'Communication et recherche de fonds'],
        ['prestations_socio_educatives', 'Socio-educational services', 'Sozialpädagogische Leistungen', 'Prestations socio-éducatives'],
        ['therapeutique_sante', 'Therapy and health', 'Therapie und Gesundheit', 'Thérapeutique et santé'],
        ['enseignement_specialise', 'Special education', 'Sonderpädagogik', 'Enseignement spécialisé'],
        ['intendance_exploitation', 'Facilities and operations', 'Hauswirtschaft und Betrieb', 'Intendance et exploitation'],
        ['autre', 'Other', 'Andere', 'Autre'],
      ],
    },
    {
      key: 'seniority', en: 'Seniority', de: 'Hierarchiestufe', fr: 'Niveau hiérarchique',
      type: 'band', kind: 'estimate',
      sources_hint: 'The same TITLE_MAP: président·e, vice-président·e, membre du comité / du conseil de fondation / du CA / Stiftungsrat / Vorstand → gouvernance; directeur·trice (général·e), secrétaire général·e, codirection, gérant·e, Geschäftsführer·in → direction; directeur·trice adjoint·e, sous-directeur·trice → direction_adjointe; chef·fe de service, responsable de secteur / de département / d’unité, membre de la direction → cadre_superieur; responsable d’équipe, coordinateur·trice, chef·fe d’équipe → cadre_intermediaire; every practitioner title → collaborateur; stagiaire, apprenti·e, bénévole, civiliste → en_formation_benevole.',
      bands: [
        ['gouvernance', 'Governance body', 'Gremium', 'Gouvernance', 0, 1],
        ['direction', 'Direction', 'Direktion', 'Direction', 1, 2],
        ['direction_adjointe', 'Deputy direction', 'Stellvertretende Direktion', 'Direction adjointe', 2, 3],
        ['cadre_superieur', 'Senior manager', 'Oberes Kader', 'Cadre supérieur', 3, 4],
        ['cadre_intermediaire', 'Middle manager', 'Mittleres Kader', 'Cadre intermédiaire', 4, 5],
        ['collaborateur', 'Staff', 'Mitarbeitende', 'Collaborateur·trice', 5, 6],
        ['en_formation_benevole', 'In training or volunteer', 'In Ausbildung oder freiwillig', 'En formation ou bénévole', 6, 7],
      ],
    },
    {
      key: 'buying_influence', en: 'Buying influence', de: 'Einfluss auf den Kauf', fr: 'Influence d’achat',
      type: 'enum', kind: 'estimate',
      sources_hint: 'COMPUTED by buyingInfluenceFor(seniority, function_family, signature_mode): direction → economic; gouvernance → ratifier; finances_administration at cadre_superieur or above → budget; informatique or qualite_securite_lpd at cadre or above → technical; prestations_socio_educatives / therapeutique_sante / enseignement_specialise at any rank, and any cadre_intermediaire → user; collaborateur in administration → none_known. A declared or estimated fact on the cell wins over the table.',
      values: [
        ['economic', 'Economic buyer', 'Wirtschaftlicher Entscheider', 'Décideur économique'],
        ['ratifier', 'Ratifier', 'Ratifizierende Instanz', 'Ratificateur'],
        ['budget', 'Budget holder', 'Budgetverantwortung', 'Responsable du budget'],
        ['technical', 'Technical buyer', 'Fachlicher Entscheider', 'Décideur technique'],
        ['user', 'User', 'Anwender', 'Utilisateur'],
        ['none_known', 'None known', 'Keiner bekannt', 'Aucune connue'],
      ],
    },
    {
      key: 'role_since', en: 'In role since', de: 'Im Amt seit', fr: 'En fonction depuis',
      type: 'year', kind: 'source_or_leave',
      sources_hint: '(1) the gazette publication that inscribed the register function — the mirror’s publicationDate year, the line as quote (§5.4); (2) the team page or annual report («directrice depuis 2019», «arrivée en 2024»); (3) the person’s own words in a mail or transcript — import; (4) the Surfe preview’s jobHistory[].startDate for the current employer — a proposal reviewed by hand; (5) the operator. A date is NEVER estimated. The applier mirrors the year into affiliations.started_on (1 January) when that column is NULL.',
    },
    {
      key: 'years_in_role', en: 'Years in role', de: 'Jahre im Amt', fr: 'Années en fonction',
      type: 'band', kind: 'computed',
      sources_hint: 'Nightly from role_since (or affiliations.started_on) against today with bandFor; neither held → not collected.',
      bands: [
        ['lt1', 'Under 1 year', 'Unter 1 Jahr', 'Moins d’un an', 0, 1],
        ['1_2', '1 to 2 years', '1 bis 2 Jahre', '1 à 2 ans', 1, 3],
        ['3_5', '3 to 5 years', '3 bis 5 Jahre', '3 à 5 ans', 3, 6],
        ['6_10', '6 to 10 years', '6 bis 10 Jahre', '6 à 10 ans', 6, 11],
        ['gt10', 'Over 10 years', 'Über 10 Jahre', 'Plus de 10 ans', 11, null],
      ],
    },
  ],
});

const GOVERNANCE = buildBlock({
  key: 'governance',
  en: 'Mandates and network',
  de: 'Mandate und Netzwerk',
  fr: 'Mandats et réseau',
  cells: [
    {
      key: 'register_function', en: 'Register function', de: 'Registerfunktion', fr: 'Fonction inscrite au registre',
      type: 'enum', kind: 'look_up',
      applies_when: { has_register_row: true, seniority: INSCRIBABLE },
      sources_hint: 'read from the gazette line naming the person WITH the affiliated organisation («est nommé·e président·e», «membre du conseil de fondation», «directeur·trice, avec signature …») — the persons parser, §5.4; none read when the organisation’s current register entry lists persons and this one is not among them (evidence: the entry snapshot); staff of an unregistered association → not applicable.',
      values: [
        ['president', 'President', 'Präsident·in', 'Président·e'],
        ['vice_president', 'Vice-president', 'Vizepräsident·in', 'Vice-président·e'],
        ['membre_comite', 'Committee member', 'Vorstandsmitglied', 'Membre du comité'],
        ['membre_conseil_fondation', 'Board of trustees member', 'Stiftungsratsmitglied', 'Membre du conseil de fondation'],
        ['direction_inscrite', 'Inscribed direction', 'Eingetragene Geschäftsführung', 'Direction inscrite'],
        ['secretaire', 'Secretary', 'Sekretär·in', 'Secrétaire'],
        ['tresorier', 'Treasurer', 'Kassier·in', 'Trésorier·ère'],
        ['autre', 'Other', 'Andere', 'Autre'],
        ['none', 'None', 'Keine', 'Aucune'],
      ],
    },
    {
      key: 'signature_mode', en: 'Signature', de: 'Zeichnungsberechtigung', fr: 'Mode de signature',
      type: 'enum', kind: 'look_up',
      applies_when: { cell_held: { key: 'person.governance.register_function', not: ['none'] } },
      sources_hint: 'The same gazette line («avec signature individuelle», «signature collective à deux», «sans signature», «n’est pas habilité·e à représenter») — ORC art. 119 al. 1 let. h; a line naming the function and no signature clause → not_found for this cell. Never inferred from the function.',
      values: [
        ['individuelle', 'Individual signature', 'Einzelunterschrift', 'Signature individuelle'],
        ['collective_a_deux', 'Joint signature of two', 'Kollektivunterschrift zu zweien', 'Signature collective à deux'],
        ['sans_signature', 'No signature', 'Ohne Zeichnungsberechtigung', 'Sans signature'],
      ],
    },
    {
      key: 'external_mandates', en: 'External mandates', de: 'Externe Mandate', fr: 'Mandats externes',
      type: 'enum_multi', kind: 'look_up',
      applies_when: { seniority: INSCRIBABLE },
      sources_hint: 'read from federation comité / commission pages (AVOP, Insertion Vaud, ARTISET, GREA — the federations the employer’s identity.umbrella_memberships names → federation_committee), a second governance affiliation on the record or in the mirror (→ other_board), cantonal commission lists (→ public_commission), a professional body’s committee (→ professional_association), a school’s staff page (→ teaching); none read when the person’s bio in the report says so.',
      values: [
        ['federation_committee', 'Federation committee', 'Verbandsvorstand', 'Comité de fédération'],
        ['other_board', 'Other board', 'Anderes Gremium', 'Autre conseil'],
        ['public_commission', 'Public commission', 'Öffentliche Kommission', 'Commission publique'],
        ['professional_association', 'Professional association', 'Berufsverband', 'Association professionnelle'],
        ['teaching', 'Teaching', 'Lehrtätigkeit', 'Enseignement'],
        ['none', 'None', 'Keine', 'Aucun'],
      ],
    },
    {
      key: 'board_mandates_count', en: 'Board mandates', de: 'Gremienmandate', fr: 'Mandats de gouvernance',
      type: 'int', kind: 'computed',
      sources_hint: 'Nightly: current held affiliations with kind = governance across organisations, plus a held register_function ≠ none; 0 is a counted zero only when register_function was looked up (held, none or not_found), else not collected.',
    },
    {
      key: 'shared_boards_count', en: 'Shared boards', de: 'Gemeinsame Gremien', fr: 'Conseils partagés',
      type: 'int', kind: 'computed',
      sources_hint: 'Nightly: other held persons sharing a current governance affiliation with this one; 0 is a counted zero when the person holds ≥ 1 governance affiliation, else not collected.',
    },
  ],
});

const PERSON = buildBlock({
  key: 'person',
  en: 'The person',
  de: 'Die Person',
  fr: 'La personne',
  cells: [
    {
      key: 'birth_year', en: 'Birth year', de: 'Geburtsjahr', fr: 'Année de naissance',
      type: 'year', kind: 'source_or_leave',
      sources_hint: 'read ONLY when a public source states the year («née en 1972») or an age with a date («Prénom Nom, 41 ans», the article’s date → the year, qualifier approx because an age spans two years); the sentence as quote, the snapshot as evidence. Never estimated, never from Surfe, never from LinkedIn. ALWAYS reviewed by hand (§3.5 excludes it from held on arrival).',
    },
    {
      key: 'birth_decade', en: 'Birth decade', de: 'Geburtsjahrzehnt', fr: 'Décennie de naissance',
      type: 'band', kind: 'estimate',
      sources_hint: 'COMPUTED from a held birth_year (no mark); else estimated 0.6 from a quoted diploma or graduation year on a public CV, an HES alumni page or a repository record (first diploma at 22–27 → a window; a window straddling a decade edge → the decade it mostly covers, at 0.4); else estimated 0.5 from a quoted career start («30 ans de métier»); nothing quotable → open, and the not_found verdict is allowed here (the one ESTIMATE cell where it is, because the basis must be public). ALWAYS reviewed by hand. Never passed to Signet, never read by draft.',
      bands: [
        ['before_1950', 'Before 1950', 'Vor 1950', 'Avant 1950', null, 1950],
        ['1950s', '1950s', '1950er', 'Années 1950', 1950, 1960],
        ['1960s', '1960s', '1960er', 'Années 1960', 1960, 1970],
        ['1970s', '1970s', '1970er', 'Années 1970', 1970, 1980],
        ['1980s', '1980s', '1980er', 'Années 1980', 1980, 1990],
        ['1990s', '1990s', '1990er', 'Années 1990', 1990, 2000],
        ['2000s_plus', '2000 and later', '2000 und später', '2000 et après', 2000, null],
      ],
    },
    {
      key: 'languages', en: 'Languages', de: 'Sprachen', fr: 'Langues',
      type: 'enum_multi', kind: 'source_or_leave',
      sources_hint: 'read from a team page line («Langues : FR, DE, EN»), a publication or talk in the language, a bilingual signature, a report bio; else COMPUTED with no mark from languageVote over the person’s OWN mails (≥ 2); else not collected — fr is never estimated from the employer’s French site.',
      values: [
        ['fr', 'French', 'Französisch', 'Français'],
        ['de', 'German', 'Deutsch', 'Allemand'],
        ['en', 'English', 'Englisch', 'Anglais'],
        ['it', 'Italian', 'Italienisch', 'Italien'],
        ['other', 'Other', 'Andere', 'Autre'],
      ],
    },
    {
      key: 'register', en: 'Register', de: 'Anrede', fr: 'Registre',
      type: 'enum', kind: 'declared',
      sources_hint: 'The existing persons.register column surfaced as a cell (NULL = not decided); the writing profile’s registerVote shown beside it as a suggestion; one click on the contact card declares it. Never proposed by an agent.',
      values: [
        ['sie', 'Formal (vous / Sie)', 'Sie', 'Vous'],
        ['du', 'Informal (tu / du)', 'Du', 'Tu'],
      ],
    },
  ],
});

const ENGAGEMENT = buildBlock({
  key: 'engagement',
  en: 'Engagement',
  de: 'Engagement',
  fr: 'Engagement',
  computed: true,
  cells: [
    {
      key: 'recency_band', en: 'Last contact', de: 'Letzter Kontakt', fr: 'Dernier contact',
      type: 'enum', kind: 'computed',
      sources_hint: 'From the latest held interaction the person took part in; never is a counted state, never "not collected".',
      values: [
        ['lt30', 'Under 30 days', 'Unter 30 Tagen', 'Moins de 30 jours'],
        ['30_90', '30 to 90 days', '30 bis 90 Tage', '30 à 90 jours'],
        ['90_180', '90 to 180 days', '90 bis 180 Tage', '90 à 180 jours'],
        ['180_365', '180 to 365 days', '180 bis 365 Tage', '180 à 365 jours'],
        ['gt365', 'Over a year', 'Über ein Jahr', 'Plus d’un an'],
        ['never', 'Never', 'Nie', 'Jamais'],
      ],
    },
    {
      key: 'touches_12m', en: 'Touches (12 months)', de: 'Kontakte (12 Monate)', fr: 'Contacts (12 mois)',
      type: 'int', kind: 'computed',
      sources_hint: 'Held interactions with the person in the last twelve months, both directions; always a number.',
    },
    {
      key: 'channel_preference', en: 'Channel preference', de: 'Bevorzugter Kanal', fr: 'Canal préféré',
      type: 'enum', kind: 'computed',
      sources_hint: 'The dominant kind of the person’s own inbound or attended interactions once ≥ 3 exist; mixed when no kind reaches half; fewer → not collected.',
      values: [
        ['mail', 'Mail', 'E-Mail', 'Courriel'],
        ['meeting', 'Meeting', 'Treffen', 'Rencontre'],
        ['phone', 'Phone', 'Telefon', 'Téléphone'],
        ['mixed', 'Mixed', 'Gemischt', 'Mixte'],
      ],
    },
    {
      key: 'response_behaviour', en: 'Response behaviour', de: 'Antwortverhalten', fr: 'Comportement de réponse',
      type: 'enum', kind: 'computed',
      sources_hint: 'replyHours median over ≥ 2 of their replies (< 24 h · < 168 h · ≥ 168 h); no_reply when ≥ 2 outbound mails in 90 days drew no inbound; else not collected.',
      values: [
        ['same_day', 'Same day', 'Am selben Tag', 'Le jour même'],
        ['within_week', 'Within a week', 'Innerhalb einer Woche', 'Dans la semaine'],
        ['slow', 'Slow', 'Langsam', 'Lent'],
        ['no_reply', 'No reply', 'Keine Antwort', 'Sans réponse'],
      ],
    },
  ],
});

/** The person raster. Five blocks, 26 cells; the last block is computed and is never proposed. */
export const PERSON_RASTER: PersonRasterBlock[] = [CONTACT, JOB, GOVERNANCE, PERSON, ENGAGEMENT];

// ── addressing ──────────────────────────────────────────────────────────────

/** `person.contact.email_work`. The one place this string is composed. */
export function personCellKey(block: string, cell: string): string {
  return `person.${block}.${cell}`;
}

export interface FoundPersonCell {
  block: PersonRasterBlock;
  cell: PersonRasterCell;
  key: string;
}

const BY_KEY = new Map<string, FoundPersonCell>();
const BY_BARE_NAME = new Map<string, FoundPersonCell | null>(); // null = the bare name is ambiguous
for (const block of PERSON_RASTER) {
  for (const cell of block.cells) {
    const key = personCellKey(block.key, cell.key);
    const found = { block, cell, key };
    BY_KEY.set(key, found);
    BY_BARE_NAME.set(cell.key, BY_BARE_NAME.has(cell.key) ? null : found);
  }
}

/**
 * `findPersonCell('person.contact.email_work')` — also `contact.email_work`, and a bare
 * `email_work` when that name is unique across the raster (§1). Null for anything else.
 */
export function findPersonCell(key: string): FoundPersonCell | null {
  const k = String(key ?? '').trim();
  if (k === '') return null;
  return BY_KEY.get(k) ?? BY_KEY.get(`person.${k}`) ?? BY_BARE_NAME.get(k) ?? null;
}

/** `findPersonBlock('contact')` — also `person.contact`. What `list_persons { missing_cell }` needs for a block test (§3.4). */
export function findPersonBlock(key: string): PersonRasterBlock | null {
  const k = String(key ?? '').trim().replace(/^person\./, '');
  return PERSON_RASTER.find((b) => b.key === k) ?? null;
}

/** Every addressable cell key, in raster order. */
export const PERSON_CELL_KEYS: string[] = [...BY_KEY.keys()];

/** The cells an agent may propose: neither computed (the night job's) nor declared (the operator's). */
export const PERSON_PROPOSABLE_KEYS: string[] = PERSON_CELL_KEYS.filter((k) => {
  const kind = BY_KEY.get(k)!.cell.kind;
  return kind !== 'computed' && kind !== 'declared';
});

/** How many cells a run may propose — the "of n cells" of the person zone. */
export const PERSON_PROPOSABLE_COUNT: number = PERSON_PROPOSABLE_KEYS.length;

/** The cells only `person_profile.compute` writes (§4) — nine of §1's twelve day-one cells. */
export const PERSON_COMPUTED_KEYS: string[] = PERSON_CELL_KEYS.filter((k) => BY_KEY.get(k)!.cell.kind === 'computed');

/** The three cells the night job fills from `TITLE_MAP` when no read/estimated/declared fact is held (§4). */
export const PERSON_TITLE_KEYS: readonly string[] = [
  personCellKey('job', 'function_family'), personCellKey('job', 'seniority'), personCellKey('job', 'buying_influence'),
];

/** The two cells that are ALWAYS a person's click (§3.5 a): never held on arrival, never passed to Signet. */
export const PERSON_BIRTH_KEYS: readonly string[] = [personCellKey('person', 'birth_year'), personCellKey('person', 'birth_decade')];

/** The comparable cells coverage counts — every block, computed included (§4 counts computed apart). */
export const PERSON_COVERED_KEYS: string[] = PERSON_CELL_KEYS.filter((k) => BY_KEY.get(k)!.cell.comparable);

// ── labels ──────────────────────────────────────────────────────────────────

export interface PersonRasterLabelSet {
  blocks: Record<string, string>;
  cells: Record<string, string>;
  values: Record<string, string>;
  kinds: Record<string, string>;
}

const EN_KINDS: Record<PersonCellKind, string> = { estimate: 'Estimate', look_up: 'Look up', source_or_leave: 'Source or leave', computed: 'Computed', declared: 'Declared' };
const DE_KINDS: Record<PersonCellKind, string> = { estimate: 'Schätzung', look_up: 'Nachschlagen', source_or_leave: 'Quelle oder leer', computed: 'Berechnet', declared: 'Erklärt' };
const FR_KINDS: Record<PersonCellKind, string> = { estimate: 'Estimation', look_up: 'À vérifier', source_or_leave: 'Source ou rien', computed: 'Calculé', declared: 'Déclaré' };

const EN_LABELS: PersonRasterLabelSet = { blocks: {}, cells: {}, values: {}, kinds: EN_KINDS };
for (const block of PERSON_RASTER) {
  EN_LABELS.blocks[block.key] = block.label;
  for (const cell of block.cells) {
    EN_LABELS.cells[`${block.key}.${cell.key}`] = cell.label;
    for (const v of [...(cell.values ?? []), ...(cell.bands ?? [])]) {
      EN_LABELS.values[`${block.key}.${cell.key}.${v.value}`] = v.label;
    }
  }
}

/** Every block, cell, value and kind label in the three console languages. */
export const PERSON_RASTER_LABELS: Record<RasterLang, PersonRasterLabelSet> = {
  en: EN_LABELS,
  de: { blocks: DE_BLOCKS, cells: DE_CELLS, values: DE_VALUES, kinds: DE_KINDS },
  fr: { blocks: FR_BLOCKS, cells: FR_CELLS, values: FR_VALUES, kinds: FR_KINDS },
};

/**
 * `personLabelFor('fr', 'job')` · `personLabelFor('fr', 'job', 'seniority')` ·
 * `personLabelFor('fr', 'job', 'seniority', 'direction')`. Falls back to English, then to the
 * raw token — a missing translation shows the English word, never an empty cell.
 */
export function personLabelFor(lang: RasterLang, block: string, cell?: string, value?: string): string {
  const path = value != null && cell != null ? `${block}.${cell}.${value}`
    : cell != null ? `${block}.${cell}`
      : block;
  const bucket = value != null ? 'values' : cell != null ? 'cells' : 'blocks';
  return PERSON_RASTER_LABELS[lang][bucket][path] ?? EN_LABELS[bucket][path] ?? (value ?? cell ?? block);
}

/** The kind badge the Review page shows (§6.3): `personKindLabel('de', 'estimate')` → "Schätzung". */
export function personKindLabel(lang: RasterLang, kind: PersonCellKind): string {
  return PERSON_RASTER_LABELS[lang].kinds[kind] ?? EN_KINDS[kind] ?? kind;
}

// ── parsing ─────────────────────────────────────────────────────────────────

/** `raster.v1`'s codes plus one: an identifier cell (e-mail, phone, LinkedIn) whose text has the wrong shape. */
export type PersonRejectionCode = RasterParseError['code'] | 'not_an_identifier';
export type PersonParseResult = RasterParseResult | { ok: false; code: 'not_an_identifier'; reason: string };

const refuse = (reason: string): PersonParseResult => ({ ok: false, code: 'not_an_identifier', reason });

// `parseCell` takes a RasterCell; a person cell is one minus `fit_relevant` (no fit axis reads
// people) and with its own `applies_when`. The view is built once per cell, not forked.
const RASTER_VIEW = new WeakMap<PersonRasterCell, RasterCell>();
function rasterView(cell: PersonRasterCell): RasterCell {
  let view = RASTER_VIEW.get(cell);
  if (!view) {
    const { kind: _kind, applies_when: _applies, ...rest } = cell;
    view = { ...rest, fit_relevant: false };
    RASTER_VIEW.set(cell, view);
  }
  return view;
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const LINKEDIN_PATH = /(?:^|[/.])linkedin\.com\/in\/([A-Za-z0-9\-_%~.]+)/i;

/**
 * Turn what a model, an importer or an operator handed us into the cell's own type, or say in
 * one actionable sentence why it is not a value for this cell — `parseCell`, unforked, plus the
 * shape §1.1 gives the three identifiers: an e-mail is an address (stored lower-cased, so the
 * `persons.emails` mirror dedupes), a phone is `+41…` normalised (`normalisePhone`, the contact
 * module's one rule), a LinkedIn URL is on `linkedin.com/in/` and is stored in its canonical
 * form. Anything else is the organisation raster's own parse.
 */
export function parsePersonCell(cell: PersonRasterCell, raw: unknown): PersonParseResult {
  const base = parseCell(rasterView(cell), raw);
  if (!base.ok || cell.type !== 'text') return base;
  const text = String(base.value);
  switch (cell.key) {
    case 'email_work': {
      const s = text.toLowerCase();
      if (!EMAIL_SHAPE.test(s)) return refuse(`${cell.label} takes an e-mail address (name@domain.tld); '${text}' is not one.`);
      return { ok: true, value: s, text: s };
    }
    case 'phone_work': {
      const p = normalisePhone(text, 'CH');
      if (!p.valid || !p.e164) return refuse(`${cell.label} takes a phone number, stored as +41…; '${text}' could not be read${p.reason ? ` (${p.reason})` : ''}.`);
      return { ok: true, value: p.e164, text: p.e164 };
    }
    case 'linkedin_url': {
      const m = LINKEDIN_PATH.exec(text);
      if (!m) return refuse(`${cell.label} takes a profile URL on linkedin.com/in/ (never a company page, never a search); '${text}' is not one.`);
      const canonical = `https://www.linkedin.com/in/${m[1].replace(/\/+$/, '')}`;
      return { ok: true, value: canonical, text: canonical };
    }
    default:
      return base;
  }
}

/** Parse by key: `parsePersonCellByKey('person.job.role_since', '2019')`. Null when the key is unknown. */
export function parsePersonCellByKey(key: string, raw: unknown): PersonParseResult | null {
  const found = findPersonCell(key);
  if (!found) return null;
  return parsePersonCell(found.cell, raw);
}

/** Which band a measured number falls in — `personBandFor(yearsInRoleCell, 4)` → '3_5'. `raster.v1`'s `bandFor`, unforked. */
export function personBandFor(cell: PersonRasterCell, n: number): string | null {
  return bandFor(rasterView(cell), n);
}

/** A seniority token's synthetic rank (its band's `min`): gouvernance 0 … en_formation_benevole 6. Null for an unknown token. */
export function seniorityRank(seniority: string | null | undefined): number | null {
  const band = JOB.cells.find((c) => c.key === 'seniority')!.bands!.find((b) => b.value === seniority);
  return band?.min ?? null;
}

/** «cadre_superieur and above» (§1.2): true when `seniority` ranks at or above `floor`. Unknown tokens are never above anything. */
export function seniorityAtOrAbove(seniority: string | null | undefined, floor: PersonSeniority): boolean {
  const a = seniorityRank(seniority);
  const f = seniorityRank(floor);
  return a !== null && f !== null && a <= f;
}

// ── applicability ───────────────────────────────────────────────────────────

/**
 * Whether a cell is asked of THIS person (§1.3). No `applies_when`, or no context → asked. A
 * condition the context cannot judge (`undefined`) → asked; a condition it can judge and that
 * fails → not applicable.
 */
export function personAppliesTo(cell: Pick<PersonRasterCell, 'applies_when'>, ctx?: PersonApplicabilityContext | null): boolean {
  const w = cell.applies_when;
  if (!w || !ctx) return true;
  if (w.has_register_row && ctx.has_register_row === false) return false;
  if (w.seniority && ctx.seniority && seniorityRank(ctx.seniority) !== null && !w.seniority.includes(ctx.seniority as PersonSeniority)) return false;
  if (w.cell_held) {
    // Only `register_function` gates a cell today; the key is kept on the rule so a second gate
    // reads the same way. `null` = looked, nothing held → the gated cell is not asked.
    const held = w.cell_held.key === personCellKey('governance', 'register_function') ? ctx.register_function : undefined;
    if (held === null) return false;
    if (held !== undefined && w.cell_held.not?.includes(held)) return false;
  }
  return true;
}

/** The comparable keys a person is measured on: PERSON_COVERED_KEYS minus what does not apply. */
export function personApplicableKeys(ctx?: PersonApplicabilityContext | null): string[] {
  if (!ctx) return PERSON_COVERED_KEYS;
  const out: string[] = [];
  for (const block of PERSON_RASTER) {
    for (const c of block.cells) if (c.comparable && personAppliesTo(c, ctx)) out.push(personCellKey(block.key, c.key));
  }
  return out;
}

// ── coverage ────────────────────────────────────────────────────────────────

/**
 * The five figures the person zone and the People count line print (§4, §6.2): «n held · m
 * estimated · k computed». ARITHMETIC MAKES NO CLAIM — no mark. `open` is what nobody collected;
 * a `not_found` verdict and a cell that does not apply are counted apart, never as open.
 */
export interface PersonCoverage {
  /** Facts read or declared — held by a person or by the rule. */
  held: number;
  /** Facts an agent estimated (and an operator or the rule held). */
  estimated: number;
  /** Cells `person_profile.compute` filled. */
  computed: number;
  /** Applicable cells with nothing on record. */
  open: number;
  /** Applicable comparable cells: held + estimated + computed + open + not_found. */
  total: number;
  not_found?: number;
  not_applicable?: number;
}

export interface PersonCoverageReport extends PersonCoverage {
  blocks: Record<string, PersonCoverage>;
}

/**
 * What the caller hands `personCoverage()`: the current `facts` rows (`method` says held /
 * estimated / not_found), the `persons_computed` cells as `{ key, value, method: 'computed' }`,
 * a bare list of keys (each counts as held), or a key → value map. A key that is not a covered
 * cell is ignored; an empty value is not a fill.
 */
export type PersonCoverageInput =
  | Iterable<string | { key: string; value?: string | null; method?: string | null }>
  | Record<string, unknown>;

type CoverageState = 'held' | 'estimated' | 'computed' | 'not_found';
const STATE_RANK: Record<CoverageState, number> = { held: 0, estimated: 1, computed: 2, not_found: 3 };

const isFilled = (v: unknown): boolean => v !== null && v !== undefined && String(v).trim() !== '';

function stateOf(method: unknown): CoverageState {
  switch (method) {
    case 'estimated': return 'estimated';
    case 'computed': return 'computed';
    case 'not_found': return 'not_found';
    default: return 'held'; // read, declared, or a bare key
  }
}

/** One state per covered key. A held fact wins over an estimate, an estimate over the table, the table over a verdict. */
function statesOf(facts: PersonCoverageInput): Map<string, CoverageState> {
  const out = new Map<string, CoverageState>();
  const add = (key: unknown, state: CoverageState): void => {
    const found = findPersonCell(String(key ?? ''));
    if (!found || !found.cell.comparable) return;
    const prev = out.get(found.key);
    if (prev === undefined || STATE_RANK[state] < STATE_RANK[prev]) out.set(found.key, state);
  };
  const iterable = facts as { [Symbol.iterator]?: unknown } | null;
  if (iterable && typeof iterable[Symbol.iterator] === 'function') {
    for (const entry of facts as Iterable<string | { key: string; value?: string | null; method?: string | null }>) {
      if (typeof entry === 'string') add(entry, 'held');
      else if (entry && typeof entry === 'object') {
        const state = stateOf(entry.method);
        // A not_found verdict has no value by design; every other state needs one to count.
        if (state !== 'not_found' && 'value' in entry && !isFilled(entry.value)) continue;
        add(entry.key, state);
      }
    }
  } else if (facts && typeof facts === 'object') {
    for (const [key, value] of Object.entries(facts as Record<string, unknown>)) if (isFilled(value)) add(key, 'held');
  }
  return out;
}

function figures(keys: readonly string[], states: Map<string, CoverageState>, notApplicable: number): PersonCoverage {
  const c: PersonCoverage = { held: 0, estimated: 0, computed: 0, open: 0, total: keys.length };
  let notFound = 0;
  for (const key of keys) {
    switch (states.get(key)) {
      case 'held': c.held += 1; break;
      case 'estimated': c.estimated += 1; break;
      case 'computed': c.computed += 1; break;
      case 'not_found': notFound += 1; break;
      default: c.open += 1;
    }
  }
  if (notFound) c.not_found = notFound;
  if (notApplicable) c.not_applicable = notApplicable;
  return c;
}

/** Coverage over the whole person raster and per block. Only comparable cells; computed cells are counted, apart. */
export function personCoverage(facts: PersonCoverageInput, ctx?: PersonApplicabilityContext | null): PersonCoverageReport {
  const states = statesOf(facts);
  const blocks: Record<string, PersonCoverage> = {};
  const allKeys: string[] = [];
  let allNotApplicable = 0;
  for (const block of PERSON_RASTER) {
    const comparable = block.cells.filter((c) => c.comparable);
    const asked = comparable.filter((c) => personAppliesTo(c, ctx)).map((c) => personCellKey(block.key, c.key));
    const notApplicable = comparable.length - asked.length;
    blocks[block.key] = figures(asked, states, notApplicable);
    allKeys.push(...asked);
    allNotApplicable += notApplicable;
  }
  return { ...figures(allKeys, states, allNotApplicable), blocks };
}

// ── §1.6 TITLE_MAP ──────────────────────────────────────────────────────────
//
// 1'422 of 1'608 persons on production carry a role title, in dozens of spellings: «Directrice»,
// «directeur général», «Directeur-trice adjoint-e», «Mitglied des Stiftungsrates», «membre du CA».
// The map is a REVIEWED dictionary — every entry was read against the live list or the Vaud
// CCT social parapublic function catalogue — and `normaliseTitle` is the one spelling rule that
// lets a dictionary of a few hundred entries answer a thousand titles. What it folds:
//   · case and accents (Éducatrice → educatrice → educateur);
//   · the inclusive forms — «·e», «(e)», «-trice», «/euse», «·in», BinnenI — down to the head word;
//   · the feminine to the masculine head word (directrice → directeur, cheffe → chef, Leiterin →
//     Leiter), so the map is written once per FUNCTION and never once per gender;
//   · hyphens and apostrophes to spaces, articles and prepositions out («membre du conseil de
//     fondation» and «membre conseil fondation» are one key).
// A separator («,», «;», «/», «&», « et », « und ») survives normalisation so `mapTitle` can apply
// the compound rule: the FIRST named function wins. A title the map does not name whole is read by
// its head PHRASE — the longest prefix of a segment the map DOES name, so «psychologue du travail»
// is a psychologue and «infirmière cheffe de service» an infirmière cheffe (the review of
// 07.09.2026) — and only then by the seniority its head noun gives (a «directeur des opérations» is
// still a direction), else `collaborateur`. Neither is `matched`: the caller logs the title for
// review, never holds it silently.

export interface TitleMapping {
  function_family: PersonFunctionFamily;
  seniority: PersonSeniority;
}

/** `mapTitle`'s answer. `matched: false` is the row `data/person-raster/unmapped-titles.log` gets. */
export interface MappedTitle extends TitleMapping {
  matched: boolean;
  /**
   * WHAT THE ANSWER RESTS ON (the deploy session's unmapped-title list, 07.09.2026). `exact` — the
   * whole title is a map entry; `segment` — one segment of a compound title is; `prefix` — the head
   * phrase of a segment is; `head` — only the first word's rank is known; `none` — NOTHING matched,
   * and the pair below is the type's default, not a reading. The live list held «Yverdon», «site de
   * Pomy», «Signataire autorisé»: places and a signature clause, filed as role titles, which used to
   * come back `autre` / `collaborateur` and be written as a function and a rank. A caller that
   * WRITES a cell must ask for `basis !== 'none'` first; a caller that only sorts may ignore it.
   */
  basis: 'exact' | 'segment' | 'prefix' | 'head' | 'none';
  /** Which comma-separated segment answered; -1 when none did. The affiliation's KIND reads it. */
  segment: number;
}

const INCLUSIVE_SUFFIXES = 'trices|trice|rices|rice|euses|euse|elles|elle|eres|ere|sses|sse|ales|ale|innen|nes|ne|les|le|ses|se|ves|ve|fes|fe|es|e|in|nen|s|r|x';
// «directeur·trice», «chef.fe», «adjoint-e», «Leiter*in», «Leiter:in», «Leiter_in», «directeur/trice»
const INCLUSIVE_MARK = new RegExp(`(?<=[a-z])[·•⋅.*:_/\\-]\\s?(?:${INCLUSIVE_SUFFIXES})(?=$|[^a-z])`, 'g');
// «chargé(e)», «directeur(trice)», «éducateur(-trice)»
const INCLUSIVE_PAREN = new RegExp(`\\(\\s?-?(?:${INCLUSIVE_SUFFIXES})\\s?\\)`, 'g');

/** Feminine → masculine head word, after accents are folded. One entry per word this market's titles use. */
const FEMININE_FOLDS: Record<string, string> = {
  // French
  directrice: 'directeur', codirectrice: 'codirecteur', presidente: 'president', vicepresidente: 'vicepresident',
  gerante: 'gerant', cheffe: 'chef', adjointe: 'adjoint', generale: 'general', sociale: 'social', specialisee: 'specialise',
  administrative: 'administratif', chargee: 'charge', coordinatrice: 'coordinateur', educatrice: 'educateur',
  assistante: 'assistant', maitresse: 'maitre', socioprofessionnelle: 'socioprofessionnel', infirmiere: 'infirmier',
  enseignante: 'enseignant', intendante: 'intendant', cuisiniere: 'cuisinier', apprentie: 'apprenti', tresoriere: 'tresorier',
  administratrice: 'administrateur', fondatrice: 'fondateur', conseillere: 'conseiller', collaboratrice: 'collaborateur',
  animatrice: 'animateur', formatrice: 'formateur', travailleuse: 'travailleur', deleguee: 'delegue', employee: 'employe',
  informaticienne: 'informaticien', technicienne: 'technicien', praticienne: 'praticien', psychomotricienne: 'psychomotricien',
  veilleuse: 'veilleur', soignante: 'soignant', comptable: 'comptable', gestionnaire: 'gestionnaire', secretaire: 'secretaire',
  aumoniere: 'aumonier', juriste: 'juriste', volontaire: 'volontaire', pedagogique: 'pedagogique', financiere: 'financier',
  operationnelle: 'operationnel', executive: 'executif', medicale: 'medical', therapeutique: 'therapeutique',
  // German (the «·in» is stripped first; a plain feminine is folded here)
  geschaftsfuhrerin: 'geschaftsfuhrer', leiterin: 'leiter', bereichsleiterin: 'bereichsleiter', teamleiterin: 'teamleiter',
  heimleiterin: 'heimleiter', gesamtleiterin: 'gesamtleiter', prasidentin: 'prasident', vizeprasidentin: 'vizeprasident',
  direktorin: 'direktor', stiftungsratin: 'stiftungsrat', sozialpadagogin: 'sozialpadagoge', sozialpadagog: 'sozialpadagoge',
  buchhalterin: 'buchhalter', kassierin: 'kassier', aktuarin: 'aktuar', praktikantin: 'praktikant', arztin: 'arzt',
  kochin: 'koch', mitarbeiterin: 'mitarbeiter', sozialarbeiterin: 'sozialarbeiter', pflegefachfrau: 'pflegefachperson',
  pflegefachmann: 'pflegefachperson', lernende: 'lernende', freiwillige: 'freiwillige', freiwilliger: 'freiwillige',
  zivildienstleistende: 'zivildienstleistende', zivildienstleistender: 'zivildienstleistende', stellvertreterin: 'stellvertreter',
  vorsitzende: 'vorsitzende', vorsitzender: 'vorsitzende',
};

/** Articles and prepositions that carry no function. «et» / «und» / «and» are NOT here — they separate a compound. */
const STOP_WORDS = new Set([
  'de', 'du', 'des', 'd', 'la', 'le', 'les', 'l', 'au', 'aux', 'a', 'en', 'pour', 'chez', 'dans',
  'der', 'die', 'das', 'des', 'dem', 'den', 'fur', 'von', 'vom', 'zum', 'zur', 'im',
  'of', 'the', 'for', 'at',
]);

/** Two spellings of one compound word this market writes both ways. */
const WORD_FOLDS: Record<string, string> = {
  'socio professionnel': 'socioprofessionnel', 'socio educatif': 'socioeducatif', 'socio educative': 'socioeducatif',
  'vice president': 'vicepresident', 'vize prasident': 'vizeprasident', 'co directeur': 'codirecteur', 'co direction': 'codirection',
  'sous directeur': 'sousdirecteur', 'ressources humaines': 'rh', 'human resources': 'rh', 'personalwesen': 'rh', 'personal': 'rh',
  'systemes information': 'informatique', 'it': 'informatique', 'informatik': 'informatique',
};

const foldAccents = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ß/g, 'ss').replace(/œ/g, 'oe').replace(/æ/g, 'ae');

/**
 * The one spelling rule behind `TITLE_MAP` (§1.6). Lower-case, accents folded, inclusive marks and
 * the feminine folded to the head word, hyphens and apostrophes to spaces, articles out,
 * whitespace collapsed. Separators («,», «;», «/», «&», «et», «und», «and») are kept for the
 * compound rule; `mapTitle` splits on them.
 */
export function normaliseTitle(title: string | null | undefined): string {
  let s = foldAccents(String(title ?? '').toLowerCase());
  // Inclusive marks: loop, because «éducateur·trice·s» carries two and «(e)s» a tail.
  for (let i = 0; i < 3; i += 1) {
    const before = s;
    s = s.replace(INCLUSIVE_PAREN, '').replace(INCLUSIVE_MARK, '');
    if (s === before) break;
  }
  s = s.replace(/[’'`´]/g, ' ').replace(/-/g, ' ');
  // Separators survive as a single «,» so the compound rule reads one shape.
  s = s.replace(/\s*[,;/&+]\s*/g, ' , ').replace(/\s+(?:et|und|and|ainsi que|sowie)\s+/g, ' , ');
  s = s.replace(/[^a-z0-9,\s]/g, ' ');
  // The word pass first (articles out, feminine → head word), THEN the compound folds — so
  // «vice-présidente du conseil» is «vice president conseil» before «vice president» becomes one word.
  const words = s.split(/\s+/).filter((w) => w !== '' && !STOP_WORDS.has(w)).map((w) => FEMININE_FOLDS[w] ?? w);
  s = words.join(' ');
  for (const [from, to] of Object.entries(WORD_FOLDS)) s = s.replace(new RegExp(`(?<![a-z])${from}(?![a-z])`, 'g'), to);
  return s.replace(/\s*,\s*/g, ' , ').replace(/^(?:,\s*)+|(?:\s*,)+$/g, '').replace(/(?:\s*,\s*){2,}/g, ' , ').trim();
}

/**
 * The forms this market writes when a title is NOT KNOWN (the review of 07.09.2026). They are a
 * verdict, not a function: a caller must ask `isNoTitle` BEFORE `mapTitle` (the compute job does)
 * so an affiliation with no title stays "not collected" instead of being filed `autre` /
 * `collaborateur` like a title the map has simply never read.
 *
 * Written as `normaliseTitle` folds them, and also as a plain fold: the spelling rule drops «à» as
 * an article and reads «/» as a compound separator, so «à définir» and «N/A» never reach the map in
 * the shape they were typed. Both readings are tested against this one list.
 */
const NO_TITLE_FORMS: ReadonlySet<string> = new Set([
  'role non precise', 'non precise', 'inconnu', 'unknown', 'n a', 'na', 'tbd', 'a definir', 'a preciser',
]);

/** Only dashes, punctuation or symbols — «—», «-», «–», «?»: a placeholder, never a function. */
const ONLY_PUNCTUATION = /^[\s\p{P}\p{S}]*$/u;

/** Lower-case, accents folded, anything else to one space: «à définir» → «a definir», «N/A» → «n a». */
const foldPlain = (s: string): string => foldAccents(s.toLowerCase()).replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * Is this "title" the absence of one? Empty or blank, a dash or a question mark, «Rôle non
 * précisé», «inconnu», «N/A», «à définir» (the review of 07.09.2026). `mapTitle` still answers such
 * a string `autre` / `collaborateur` / `matched: false` as it always has — this is the predicate
 * that lets a caller not ask it at all, and not log a non-title as an unmapped one.
 */
export function isNoTitle(title: string | null | undefined): boolean {
  const raw = String(title ?? '').trim();
  if (raw === '' || ONLY_PUNCTUATION.test(raw)) return true;
  return NO_TITLE_FORMS.has(normaliseTitle(raw)) || NO_TITLE_FORMS.has(foldPlain(raw));
}

// The seed: [title as it is written, function family, seniority]. The first eighteen are the
// production list of 06.09.2026 with their counts; then the Vaud CCT social parapublic function
// catalogue's usual titles; then the register's function words and the German forms. Written in
// natural spelling — `normaliseTitle` keys them — so a reviewer reads titles, not tokens.
const TITLE_SEED: readonly (readonly [title: string, function_family: PersonFunctionFamily, seniority: PersonSeniority])[] = [
  // ── production, 06.09.2026 ──
  ['membre du conseil de fondation', 'gouvernance', 'gouvernance'],            // 82
  ['membre du comité', 'gouvernance', 'gouvernance'],                          // 69
  ['directeur', 'direction_generale', 'direction'],                            // 63 (+ directrice 52)
  ['président du conseil de fondation', 'gouvernance', 'gouvernance'],         // 57 (+ présidente 27)
  ['président du comité', 'gouvernance', 'gouvernance'],                       // 38 (+ présidente 23)
  ['membre du CA', 'gouvernance', 'gouvernance'],                              // 36
  ['directeur général', 'direction_generale', 'direction'],                    // 32 (+ directrice générale 12)
  ['président du CA', 'gouvernance', 'gouvernance'],                           // 24
  ['vice-président conseil fondation', 'gouvernance', 'gouvernance'],          // 18
  ['président', 'gouvernance', 'gouvernance'],                                 // 14
  ['gérant', 'direction_generale', 'direction'],                               // 10
  ['médecin', 'therapeutique_sante', 'collaborateur'],                         // 10
  ['Mitglied des Vorstandes', 'gouvernance', 'gouvernance'],                   // 10
  ['Mitglied des Stiftungsrates', 'gouvernance', 'gouvernance'],               // 8
  // ── governance, the register's function words (§1.3) ──
  ['vice-président', 'gouvernance', 'gouvernance'],
  ['vice-président du comité', 'gouvernance', 'gouvernance'],
  ['vice-président du CA', 'gouvernance', 'gouvernance'],
  ['membre du conseil d’administration', 'gouvernance', 'gouvernance'],
  ['membre du conseil', 'gouvernance', 'gouvernance'],
  ['membre du bureau', 'gouvernance', 'gouvernance'],
  // A bare «membre» stays governance (the review of 07.09.2026): every live «membre …» title on the
  // record is a board seat, and the head-phrase rule reads «membre du bureau exécutif» through it.
  ['membre', 'gouvernance', 'gouvernance'],
  ['administrateur', 'gouvernance', 'gouvernance'],
  ['président du conseil d’administration', 'gouvernance', 'gouvernance'],
  ['président de la fondation', 'gouvernance', 'gouvernance'],
  ['président de l’association', 'gouvernance', 'gouvernance'],
  ['trésorier', 'gouvernance', 'gouvernance'],
  ['secrétaire du comité', 'gouvernance', 'gouvernance'],
  ['Präsident', 'gouvernance', 'gouvernance'],
  ['Vizepräsident', 'gouvernance', 'gouvernance'],
  ['Stiftungsrat', 'gouvernance', 'gouvernance'],
  ['Stiftungsratspräsident', 'gouvernance', 'gouvernance'],
  ['Präsident des Stiftungsrates', 'gouvernance', 'gouvernance'],
  ['Vorstandsmitglied', 'gouvernance', 'gouvernance'],
  ['Präsident des Vorstandes', 'gouvernance', 'gouvernance'],
  ['Vorstand', 'gouvernance', 'gouvernance'],
  ['Kassier', 'gouvernance', 'gouvernance'],
  ['Aktuar', 'gouvernance', 'gouvernance'],
  ['Verwaltungsrat', 'gouvernance', 'gouvernance'],
  ['Mitglied des Verwaltungsrates', 'gouvernance', 'gouvernance'],
  ['board member', 'gouvernance', 'gouvernance'],
  ['chairman', 'gouvernance', 'gouvernance'],
  ['chair', 'gouvernance', 'gouvernance'],
  ['treasurer', 'gouvernance', 'gouvernance'],
  // ── direction ──
  ['direction', 'direction_generale', 'direction'],
  ['directeur général adjoint', 'direction_generale', 'direction_adjointe'],
  ['directeur·trice adjoint·e', 'direction_generale', 'direction_adjointe'],
  ['sous-directeur·trice', 'direction_generale', 'direction_adjointe'],
  ['adjoint·e de direction', 'direction_generale', 'direction_adjointe'],
  ['secrétaire général·e', 'direction_generale', 'direction'],
  ['codirecteur', 'direction_generale', 'direction'],
  ['codirection', 'direction_generale', 'direction'],
  // A founder on a team page RUNS the house, they do not sit on its board (the review of
  // 07.09.2026): a bare «Fondatrice» is a direction, and a founder who is only president is filed
  // by «président» — which the head-phrase rule reads out of «président fondateur».
  ['fondateur', 'direction_generale', 'direction'],
  ['directeur exécutif', 'direction_generale', 'direction'],
  // THE EXECUTIVE BODY IS NOT A BOARD (the deploy session's dry run, 07.09.2026). «Comité de
  // direction», CODIR and «Generaldirektion» are the body that RUNS the house; only the head noun
  // was read («comité», «président»), so twenty-one executives were about to be backfilled as
  // governance seats. The rule the operator's own case set stands: `gouvernance` is a SUPERVISORY
  // seat — conseil de fondation, comité, conseil d'administration — and whoever runs a department
  // and holds a budget is `direction`. In an intercommunal association the comité de direction is
  // the executive organ and its members may be elected officials rather than staff: the seat they
  // hold HERE is still executive, and any outside mandate is the `external_mandates` cell's to
  // find, never a title's to infer.
  ['comité de direction', 'direction_generale', 'direction'],
  ['membre du comité de direction', 'direction_generale', 'direction'],
  ['président du comité de direction', 'direction_generale', 'direction'],
  ['vice-président du comité de direction', 'direction_generale', 'direction'],
  ['trésorier du comité de direction', 'finances_administration', 'direction'],
  ['secrétaire du comité de direction', 'finances_administration', 'cadre_superieur'],
  ['codir', 'direction_generale', 'direction'],
  ['membre du codir', 'direction_generale', 'direction'],
  ['président du codir', 'direction_generale', 'direction'],
  ['président de la direction', 'direction_generale', 'direction'],
  ['generaldirektion', 'direction_generale', 'direction'],
  ['präsident der generaldirektion', 'direction_generale', 'direction'],
  ['mitglied der generaldirektion', 'direction_generale', 'direction'],
  ['membre de la direction', 'direction_generale', 'cadre_superieur'],
  ['membre de la direction générale', 'direction_generale', 'cadre_superieur'],
  ['directeur financier', 'finances_administration', 'direction'],
  ['directeur administratif', 'finances_administration', 'direction'],
  ['directeur administratif et financier', 'finances_administration', 'direction'],
  ['directeur des finances', 'finances_administration', 'direction'],
  ['directeur des ressources humaines', 'ressources_humaines', 'direction'],
  ['directeur pédagogique', 'prestations_socio_educatives', 'direction'],
  ['directeur des soins', 'therapeutique_sante', 'direction'],
  ['directeur médical', 'therapeutique_sante', 'direction'],
  ['directeur informatique', 'informatique', 'direction'],
  ['directeur des opérations', 'intendance_exploitation', 'direction'],
  ['Geschäftsführer·in', 'direction_generale', 'direction'],
  ['Direktor', 'direction_generale', 'direction'],
  ['Heimleiter', 'direction_generale', 'direction'],
  ['Gesamtleiter', 'direction_generale', 'direction'],
  ['Geschäftsleiter', 'direction_generale', 'direction'],
  ['Mitglied der Geschäftsleitung', 'direction_generale', 'cadre_superieur'],
  ['Stellvertretender Geschäftsführer', 'direction_generale', 'direction_adjointe'],
  ['Stellvertretender Direktor', 'direction_generale', 'direction_adjointe'],
  ['Leiter Finanzen', 'finances_administration', 'direction'],
  ['CEO', 'direction_generale', 'direction'],
  ['managing director', 'direction_generale', 'direction'],
  ['executive director', 'direction_generale', 'direction'],
  ['secretary general', 'direction_generale', 'direction'],
  ['CFO', 'finances_administration', 'direction'],
  ['COO', 'intendance_exploitation', 'direction'],
  ['CIO', 'informatique', 'direction'],
  ['CTO', 'informatique', 'direction'],
  ['head of finance', 'finances_administration', 'direction'],
  // ── the Vaud CCT social parapublic function catalogue: cadres ──
  ['chef·fe de service', 'autre', 'cadre_superieur'],
  ['responsable de secteur', 'autre', 'cadre_superieur'],
  ['responsable de département', 'autre', 'cadre_superieur'],
  ['responsable d’unité', 'autre', 'cadre_superieur'],
  ['responsable de site', 'autre', 'cadre_superieur'],
  ['responsable de pôle', 'autre', 'cadre_superieur'],
  ['responsable de programme', 'autre', 'cadre_superieur'],
  ['responsable pédagogique', 'prestations_socio_educatives', 'cadre_superieur'],
  ['responsable socio-éducatif', 'prestations_socio_educatives', 'cadre_superieur'],
  ['responsable des soins', 'therapeutique_sante', 'cadre_superieur'],
  ['responsable RH', 'ressources_humaines', 'cadre_superieur'],
  ['responsable des ressources humaines', 'ressources_humaines', 'cadre_superieur'],
  ['responsable finances', 'finances_administration', 'cadre_superieur'],
  ['responsable financier', 'finances_administration', 'cadre_superieur'],
  ['responsable administratif·ve', 'finances_administration', 'cadre_superieur'],
  ['responsable administratif et financier', 'finances_administration', 'cadre_superieur'],
  ['responsable comptable', 'finances_administration', 'cadre_superieur'],
  ['responsable de la comptabilité', 'finances_administration', 'cadre_superieur'],
  ['chef comptable', 'finances_administration', 'cadre_superieur'],
  ['responsable informatique', 'informatique', 'cadre_superieur'],
  ['responsable des systèmes d’information', 'informatique', 'cadre_superieur'],
  ['responsable IT', 'informatique', 'cadre_superieur'],
  ['responsable qualité', 'qualite_securite_lpd', 'cadre_superieur'],
  ['responsable sécurité', 'qualite_securite_lpd', 'cadre_superieur'],
  ['responsable santé et sécurité', 'qualite_securite_lpd', 'cadre_superieur'],
  ['responsable qualité et sécurité', 'qualite_securite_lpd', 'cadre_superieur'],
  ['délégué·e à la protection des données', 'qualite_securite_lpd', 'cadre_superieur'],
  ['conseiller à la protection des données', 'qualite_securite_lpd', 'cadre_superieur'],
  ['DPO', 'qualite_securite_lpd', 'cadre_superieur'],
  ['responsable communication', 'communication_recherche_fonds', 'cadre_superieur'],
  ['responsable de la communication', 'communication_recherche_fonds', 'cadre_superieur'],
  ['responsable de la recherche de fonds', 'communication_recherche_fonds', 'cadre_superieur'],
  ['responsable recherche de fonds', 'communication_recherche_fonds', 'cadre_superieur'],
  ['responsable du développement', 'communication_recherche_fonds', 'cadre_superieur'],
  ['responsable de l’intendance', 'intendance_exploitation', 'cadre_superieur'],
  ['responsable logistique', 'intendance_exploitation', 'cadre_superieur'],
  ['responsable technique', 'intendance_exploitation', 'cadre_superieur'],
  ['responsable de la formation', 'enseignement_specialise', 'cadre_superieur'],
  ['Leiter·in', 'autre', 'cadre_superieur'],
  ['Bereichsleiter·in', 'autre', 'cadre_superieur'],
  ['Abteilungsleiter', 'autre', 'cadre_superieur'],
  ['Leiter Personal', 'ressources_humaines', 'cadre_superieur'],
  ['Leiter HR', 'ressources_humaines', 'cadre_superieur'],
  ['Leiter Informatik', 'informatique', 'cadre_superieur'],
  ['Leiter Kommunikation', 'communication_recherche_fonds', 'cadre_superieur'],
  ['Leiter Qualität', 'qualite_securite_lpd', 'cadre_superieur'],
  ['Leiter Pflege', 'therapeutique_sante', 'cadre_superieur'],
  ['Leiter Hauswirtschaft', 'intendance_exploitation', 'cadre_superieur'],
  ['Pflegedienstleiter', 'therapeutique_sante', 'cadre_superieur'],
  ['head of IT', 'informatique', 'cadre_superieur'],
  // ── cadres intermédiaires ──
  ['responsable d’équipe', 'autre', 'cadre_intermediaire'],
  ['chef·fe d’équipe', 'autre', 'cadre_intermediaire'],
  ['coordinateur·trice', 'autre', 'cadre_intermediaire'],
  ['responsable de groupe', 'prestations_socio_educatives', 'cadre_intermediaire'],
  ['responsable de foyer', 'prestations_socio_educatives', 'cadre_intermediaire'],
  ['responsable d’atelier', 'prestations_socio_educatives', 'cadre_intermediaire'],
  ['responsable de mesure', 'prestations_socio_educatives', 'cadre_intermediaire'],
  ['intendant·e', 'intendance_exploitation', 'cadre_intermediaire'],
  ['chef·fe de cuisine', 'intendance_exploitation', 'cadre_intermediaire'],
  ['Teamleiter', 'autre', 'cadre_intermediaire'],
  ['Gruppenleiter', 'prestations_socio_educatives', 'cadre_intermediaire'],
  ['Wohngruppenleiter', 'prestations_socio_educatives', 'cadre_intermediaire'],
  ['Werkstattleiter', 'prestations_socio_educatives', 'cadre_intermediaire'],
  // ── the practitioners: collaborateur ──
  ['chargé·e de communication', 'communication_recherche_fonds', 'collaborateur'],
  ['chargé·e de recherche de fonds', 'communication_recherche_fonds', 'collaborateur'],
  ['chargé·e de projet', 'autre', 'collaborateur'],
  ['éducateur·trice social·e', 'prestations_socio_educatives', 'collaborateur'],
  ['éducateur·trice', 'prestations_socio_educatives', 'collaborateur'],
  ['éducateur·trice spécialisé·e', 'prestations_socio_educatives', 'collaborateur'],
  ['assistant·e social·e', 'prestations_socio_educatives', 'collaborateur'],
  ['travailleur·euse social·e', 'prestations_socio_educatives', 'collaborateur'],
  ['maître·sse socioprofessionnel·le', 'prestations_socio_educatives', 'collaborateur'],
  ['animateur·trice socioculturel·le', 'prestations_socio_educatives', 'collaborateur'],
  ['animateur·trice', 'prestations_socio_educatives', 'collaborateur'],
  ['conseiller·ère en insertion', 'prestations_socio_educatives', 'collaborateur'],
  ['job coach', 'prestations_socio_educatives', 'collaborateur'],
  ['veilleur·euse', 'prestations_socio_educatives', 'collaborateur'],
  ['infirmier·ère', 'therapeutique_sante', 'collaborateur'],
  ['infirmier·ère chef·fe', 'therapeutique_sante', 'cadre_intermediaire'],
  ['aide-soignant·e', 'therapeutique_sante', 'collaborateur'],
  ['psychologue', 'therapeutique_sante', 'collaborateur'],
  ['psychiatre', 'therapeutique_sante', 'collaborateur'],
  ['physiothérapeute', 'therapeutique_sante', 'collaborateur'],
  ['ergothérapeute', 'therapeutique_sante', 'collaborateur'],
  ['logopédiste', 'therapeutique_sante', 'collaborateur'],
  ['psychomotricien·ne', 'therapeutique_sante', 'collaborateur'],
  ['enseignant·e spécialisé·e', 'enseignement_specialise', 'collaborateur'],
  ['enseignant·e', 'enseignement_specialise', 'collaborateur'],
  ['formateur·trice', 'enseignement_specialise', 'collaborateur'],
  ['formateur·trice d’adultes', 'enseignement_specialise', 'collaborateur'],
  ['comptable', 'finances_administration', 'collaborateur'],
  ['assistant·e de direction', 'finances_administration', 'collaborateur'],
  ['adjoint·e administratif·ve', 'finances_administration', 'collaborateur'],
  ['collaborateur·trice administratif·ve', 'finances_administration', 'collaborateur'],
  ['secrétaire', 'finances_administration', 'collaborateur'],
  ['secrétaire de direction', 'finances_administration', 'collaborateur'],
  ['réceptionniste', 'finances_administration', 'collaborateur'],
  ['gestionnaire RH', 'ressources_humaines', 'collaborateur'],
  ['assistant·e RH', 'ressources_humaines', 'collaborateur'],
  ['informaticien·ne', 'informatique', 'collaborateur'],
  ['technicien·ne informatique', 'informatique', 'collaborateur'],
  ['cuisinier·ère', 'intendance_exploitation', 'collaborateur'],
  ['concierge', 'intendance_exploitation', 'collaborateur'],
  ['chauffeur', 'intendance_exploitation', 'collaborateur'],
  ['agent·e d’exploitation', 'intendance_exploitation', 'collaborateur'],
  ['collaborateur·trice', 'autre', 'collaborateur'],
  ['Sozialpädagog·in', 'prestations_socio_educatives', 'collaborateur'],
  ['Sozialarbeiter', 'prestations_socio_educatives', 'collaborateur'],
  ['Arbeitsagoge', 'prestations_socio_educatives', 'collaborateur'],
  ['Betreuer', 'prestations_socio_educatives', 'collaborateur'],
  ['Pflegefachperson', 'therapeutique_sante', 'collaborateur'],
  ['Arzt', 'therapeutique_sante', 'collaborateur'],
  ['Psychologe', 'therapeutique_sante', 'collaborateur'],
  ['Buchhalter', 'finances_administration', 'collaborateur'],
  ['Sachbearbeiter', 'finances_administration', 'collaborateur'],
  ['Mitarbeiter', 'autre', 'collaborateur'],
  ['Hauswart', 'intendance_exploitation', 'collaborateur'],
  ['Koch', 'intendance_exploitation', 'collaborateur'],
  // ── en formation, bénévole ──
  ['stagiaire', 'autre', 'en_formation_benevole'],
  ['apprenti·e', 'autre', 'en_formation_benevole'],
  ['bénévole', 'autre', 'en_formation_benevole'],
  ['civiliste', 'autre', 'en_formation_benevole'],
  ['volontaire', 'autre', 'en_formation_benevole'],
  ['Praktikant', 'autre', 'en_formation_benevole'],
  ['Lernende·r', 'autre', 'en_formation_benevole'],
  ['Freiwillige·r', 'autre', 'en_formation_benevole'],
  ['Zivildienstleistende·r', 'autre', 'en_formation_benevole'],
];

/** Normalised title → its pair. Built from `TITLE_SEED` through `normaliseTitle`, so the map and the rule cannot drift. */
export const TITLE_MAP: Readonly<Record<string, TitleMapping>> = (() => {
  const out: Record<string, TitleMapping> = {};
  for (const [title, function_family, seniority] of TITLE_SEED) {
    const key = normaliseTitle(title);
    if (key in out && (out[key].function_family !== function_family || out[key].seniority !== seniority)) {
      throw new Error(`person-raster.v1: TITLE_SEED maps '${title}' (${key}) twice with different pairs`);
    }
    out[key] = { function_family, seniority };
  }
  return Object.freeze(out);
})();

/**
 * The seniority a head noun gives when neither the whole title, one of its segments nor its head
 * PHRASE matches (§1.6: «autre / the seniority its head noun gives, else collaborateur»). Only
 * heads whose rank is unambiguous, and only heads the map itself does not name — «membre»,
 * «président» and «directeur» are entries of `TITLE_MAP`, so the head-phrase rule answers them
 * with a family and this last table never sees them (the review of 07.09.2026).
 */
const HEAD_SENIORITY: Record<string, PersonSeniority> = {
  president: 'gouvernance', vicepresident: 'gouvernance', prasident: 'gouvernance', vizeprasident: 'gouvernance', administrateur: 'gouvernance',
  directeur: 'direction', codirecteur: 'direction', direction: 'direction', direktor: 'direction', gerant: 'direction', geschaftsfuhrer: 'direction',
  heimleiter: 'direction', gesamtleiter: 'direction',
  sousdirecteur: 'direction_adjointe',
  chef: 'cadre_superieur', responsable: 'cadre_superieur', leiter: 'cadre_superieur', bereichsleiter: 'cadre_superieur', abteilungsleiter: 'cadre_superieur',
  coordinateur: 'cadre_intermediaire', teamleiter: 'cadre_intermediaire', gruppenleiter: 'cadre_intermediaire',
  stagiaire: 'en_formation_benevole', apprenti: 'en_formation_benevole', benevole: 'en_formation_benevole', civiliste: 'en_formation_benevole',
  volontaire: 'en_formation_benevole', praktikant: 'en_formation_benevole', lernende: 'en_formation_benevole', freiwillige: 'en_formation_benevole',
};

const UNMAPPED: MappedTitle = { function_family: 'autre', seniority: 'collaborateur', matched: false, basis: 'none', segment: -1 };

// «Directeur (ad interim)», «Éducatrice spécialisée (remplaçante)», «Responsable RH à 80 %»,
// «Concierge 50%» — a trailing parenthesis and a trailing employment rate QUALIFY a title, they
// never name a function, and normalisation keeps their words (the review of 07.09.2026). They are
// stripped from the raw title, before `normaliseTitle`, so the map is asked the head phrase; a
// title that is nothing BUT the qualifier is left exactly as it was written.
const TRAILING_PAREN = /\s*\([^()]*\)\s*$/;
const TRAILING_RATE = /\s*[,;:–—-]?\s*(?:[àa]|zu|at)?\s*\d{1,3}(?:[.,]\d+)?\s*(?:[-–—/à]\s*\d{1,3}(?:[.,]\d+)?\s*)?%\s*$/i;

function stripTitleQualifiers(raw: string): string {
  let s = raw.trim();
  // Loop: «Éducatrice spécialisée à 80 % (remplaçante)» carries both, in either order.
  for (let i = 0; i < 3; i += 1) {
    const stripped = s.replace(TRAILING_PAREN, '').replace(TRAILING_RATE, '').trim();
    if (stripped === '' || stripped === s) break;
    s = stripped;
  }
  return s;
}

/**
 * A verbatim role title → its function family and seniority (§1.6). The compound rule: a title
 * like «responsable informatique et qualité» is tried whole, then segment by segment, and the
 * FIRST segment the map names wins.
 *
 * Then the HEAD PHRASE (the review of 07.09.2026): the longest prefix of a segment's words the map
 * names answers, with `matched: false`. Before this rule a qualified title lost its family to
 * `autre` although its head was mapped — «Psychologue du travail» read as `autre`, not
 * `therapeutique_sante` — because only the head NOUN's seniority was consulted. The prefix is
 * tried longest-first so «infirmier chef» beats «infirmier», and the answer stays UNMATCHED so the
 * compute job still writes the title to `data/person-raster/unmapped-titles.log` for review.
 *
 * A title matching nothing at all answers `autre`, the seniority its head noun gives (else
 * `collaborateur`) and `matched: false`; nothing here is held silently. A title that is not a title
 * («—», «Rôle non précisé») answers the same, but a caller should ask `isNoTitle` first.
 */
export function mapTitle(title: string | null | undefined): MappedTitle {
  const norm = normaliseTitle(stripTitleQualifiers(String(title ?? '')));
  if (norm === '') return UNMAPPED;
  const whole = TITLE_MAP[norm.replace(/\s*,\s*/g, ' ')];
  if (whole) return { ...whole, matched: true, basis: 'exact', segment: 0 };
  const segments = norm.split(/\s*,\s*/).filter((s) => s !== '');
  // A GOVERNANCE SEAT NAMED AFTER THE FIRST SEGMENT IS AT ANOTHER BODY (the deploy session,
  // 07.09.2026). «Consultante juridique et Vice-Présidente du Tribunal des Prud'hommes» read as
  // `gouvernance` at her EMPLOYER, because the compound rule takes the first segment the map
  // knows and the vice-presidency was the only one. The role HERE is the one named first; a seat
  // elsewhere is the `external_mandates` cell's, and this guard keeps it out of BOTH the job
  // cells and the affiliation's kind. A later segment that is NOT a seat («Psychologue du
  // travail, directrice»; «Vice-président du conseil d'administration; CEO») is another hat at
  // the same employer and still answers — the strongest hat is what a seller needs.
  const elsewhere = (mapping: TitleMapping, segment: number): boolean => segment > 0 && mapping.seniority === 'gouvernance';
  for (let i = 0; i < segments.length; i += 1) {
    const hit = TITLE_MAP[segments[i]];
    if (hit && !elsewhere(hit, i)) return { ...hit, matched: true, basis: 'segment', segment: i };
  }
  // The head phrase, segment by segment and longest prefix first — an exact match anywhere in the
  // title has already been preferred, so a named function is never lost to a prefix of another.
  for (let i = 0; i < segments.length; i += 1) {
    const words = segments[i].split(' ').filter((w) => w !== '');
    for (let n = words.length - 1; n >= 1; n -= 1) {
      const prefix = TITLE_MAP[words.slice(0, n).join(' ')];
      if (prefix && !elsewhere(prefix, i)) return { ...prefix, matched: false, basis: 'prefix', segment: i };
    }
  }
  const head = segments[0]?.split(' ')[0] ?? '';
  const rank = HEAD_SENIORITY[head];
  // NOTHING MATCHED — not the title, not a segment, not a head phrase, not even the first word's
  // rank. The pair returned is the type's default and says nothing about this person; `basis:
  // 'none'` is how a caller knows not to write it (the deploy session's list, 07.09.2026).
  if (rank === undefined) return UNMAPPED;
  return { function_family: 'autre', seniority: rank, matched: false, basis: 'head', segment: 0 };
}

/**
 * The KIND of the affiliation a role title describes — `governance` only when the FIRST segment of
 * the title is a governance seat. THE FIRST SEGMENT IS THE ROLE HERE (the deploy session,
 * 07.09.2026): «Consultante juridique et Vice-Présidente du Tribunal des Prud'hommes» used to
 * become a governance affiliation at her EMPLOYER, because the compound rule takes the first
 * segment the map names and the vice-presidency was the only one it knew. A seat at another body
 * is an `external_mandates` look-up, never this employer's board. A title that is no title, or that
 * rests on nothing, is `employment`: the ordinary reading, never a promotion by default.
 *
 * The one rule both doors use — `utils/proposals.ts#affiliationKindOf` (the applier) and
 * `scripts/affiliation-kind-dry-run.ts` (the backfill the operator reads first).
 */
export function affiliationKindFor(roleTitle: string | null | undefined): 'employment' | 'governance' {
  if (isNoTitle(roleTitle)) return 'employment';
  // `mapTitle` already refuses a governance seat named after the first segment, so the kind and
  // the job cells cannot disagree about which body the seat is at (07.09.2026). The FIRST segment
  // still decides, because that is the role at this employer: «Vice-président du conseil
  // d'administration; CEO» is a board seat here whose holder is also the chief executive.
  const norm = normaliseTitle(stripTitleQualifiers(String(roleTitle ?? '')));
  const first = norm.split(/\s*,\s*/).filter((seg) => seg !== '')[0] ?? '';
  const mapped = mapTitle(first);
  return mapped.basis !== 'none' && mapped.seniority === 'gouvernance' ? 'governance' : 'employment';
}

// ── §1.2 buyingInfluenceFor ─────────────────────────────────────────────────

const ADMINISTRATION: readonly PersonFunctionFamily[] = ['finances_administration', 'ressources_humaines'];
const USERS: readonly PersonFunctionFamily[] = ['prestations_socio_educatives', 'therapeutique_sante', 'enseignement_specialise'];
const TECHNICAL: readonly PersonFunctionFamily[] = ['informatique', 'qualite_securite_lpd'];
const SIGNS: readonly string[] = ['individuelle', 'collective_a_deux'];

/**
 * The pure table of §1.2, row by row and in this order:
 *   direction (any function; a known signature individuelle / collective confirms it)  → economic
 *   gouvernance                                                                          → ratifier
 *   finances_administration at cadre_superieur or above                                 → budget
 *   informatique or qualite_securite_lpd at cadre (intermediaire) or above              → technical
 *   prestations / therapeutique / enseignement at any rank; any cadre_intermediaire     → user
 *   collaborateur in administration                                                     → none_known
 * A `declared` or `estimated` fact on the cell wins over this table — the caller checks that
 * first. Anything the table does not name is `none_known`: "none known" is the honest answer, a
 * guess is not. A direction_adjointe whom the register says SIGNS (individuelle or collective à
 * deux) can bind the organisation and is read as economic — the one place the signature moves a
 * row; a signature never changes any other verdict.
 */
export function buyingInfluenceFor(
  seniority: string | null | undefined,
  functionFamily: string | null | undefined,
  signatureMode: string | null | undefined,
): PersonBuyingInfluence {
  const s = (seniority ?? null) as PersonSeniority | null;
  const f = (functionFamily ?? null) as PersonFunctionFamily | null;
  if (s === 'direction') return 'economic';
  if (s === 'direction_adjointe' && signatureMode && SIGNS.includes(signatureMode)) return 'economic';
  if (s === 'gouvernance') return 'ratifier';
  if (f === 'finances_administration' && seniorityAtOrAbove(s, 'cadre_superieur')) return 'budget';
  if (f && TECHNICAL.includes(f) && seniorityAtOrAbove(s, 'cadre_intermediaire')) return 'technical';
  if (f && USERS.includes(f)) return 'user';
  if (s === 'cadre_intermediaire') return 'user';
  // A head of service or of a sector with no named domain (the bare rank titles the map answers
  // `autre` for — chef·fe de service, responsable de secteur) runs the teams that USE the systems
  // a sale is about; «none_known» would hide the sector's most common cadre from every buying
  // group (the lead's probe of 07.09.2026 over the live titles).
  if (s === 'cadre_superieur' && (f === 'autre' || f === null)) return 'user';
  if (s === 'collaborateur' && f && ADMINISTRATION.includes(f)) return 'none_known';
  return 'none_known';
}

// ── §5.5 what is excluded, in code ──────────────────────────────────────────
//
// LPD art. 5 let. c names the data that is sensitive by law: (1) religious, philosophical,
// political or trade-union opinions and activities; (2) health, the intimate sphere, racial or
// ethnic origin; (3) genetic data; (4) biometric data; (5) criminal and administrative
// proceedings or sanctions; (6) social-assistance measures. The operator's line is drawn a
// little wider (nationality and residence status beside ethnic origin). A person fact — a free
// key, a value, a quote — that names any of it is refused by `propose_fact` with the article, and
// marked on the Review page. The vocabulary is FR and DE, matched on whole words with accents
// and case folded. A word that names a FIELD OF WORK in this market («handicap», «addiction»,
// «santé mentale», «migrants») is deliberately NOT here: a person's employer's mission is not the
// person's own health, and a refusal that fires on every job description would teach the agents
// to stop filing person facts at all. «aide sociale» and «maladie» are in — the brief names them.

export interface ExcludedTopic {
  topic: 'religion' | 'politique' | 'syndicat' | 'sante' | 'intime' | 'origine' | 'genetique' | 'biometrique' | 'penal' | 'aide_sociale';
  /** The article, as the refusal names it. */
  article: string;
  /** English gloss (the i18n key the refusal and the Review mark use). */
  label: string;
  /** FR and DE words and phrases, natural spelling; matched whole-word, accents and case folded. */
  terms: readonly string[];
}

export const EXCLUDED_PERSON_TOPICS: readonly ExcludedTopic[] = [
  {
    topic: 'religion', article: 'LPD art. 5 let. c ch. 1', label: 'religious or philosophical opinion',
    terms: [
      'religion', 'religieux', 'religieuse', 'confession religieuse', 'confession', 'croyance', 'croyant', 'croyante', 'pratiquant', 'pratiquante',
      'athée', 'agnostique', 'musulman', 'musulmane', 'chrétien', 'chrétienne', 'catholique', 'protestantisme', 'évangélique', 'juif', 'juive',
      'israélite', 'bouddhiste', 'hindou', 'islam', 'judaïsme', 'christianisme',
      'Religion', 'religiös', 'Konfession', 'Glaube', 'gläubig', 'Muslim', 'Muslimin', 'muslimisch', 'katholisch', 'reformiert', 'evangelisch',
      'jüdisch', 'Jude', 'Jüdin', 'Atheist', 'Atheistin', 'buddhistisch',
    ],
  },
  {
    topic: 'politique', article: 'LPD art. 5 let. c ch. 1', label: 'political opinion',
    terms: [
      'parti politique', 'parti socialiste', 'parti libéral', 'parti libéral-radical', 'parti radical', 'parti démocrate-chrétien', 'parti ouvrier',
      'parti populaire', 'parti communiste', 'parti pirate', 'parti évangélique', 'parti bourgeois', 'parti vert', 'parti écologiste',
      'appartenance politique', 'opinion politique', 'opinions politiques', 'engagement politique', 'militant', 'militante', 'socialiste',
      'communiste', 'écologiste', 'UDC', 'PLR', 'PDC', 'POP', 'Ensemble à Gauche', 'solidaritéS',
      'Partei', 'Parteimitglied', 'Parteizugehörigkeit', 'parteipolitisch', 'politische Meinung', 'politische Einstellung', 'politisches Engagement',
      'Sozialdemokratische Partei', 'SVP', 'FDP', 'die Mitte', 'die Grünen', 'GLP', 'EVP', 'EDU',
    ],
  },
  {
    topic: 'syndicat', article: 'LPD art. 5 let. c ch. 1', label: 'trade-union membership or activity',
    terms: [
      'syndicat', 'syndicats', 'syndical', 'syndicale', 'syndiqué', 'syndiquée', 'délégué syndical', 'déléguée syndicale',
      'Gewerkschaft', 'gewerkschaftlich', 'Gewerkschafter', 'Gewerkschafterin', 'Gewerkschaftsmitglied',
    ],
  },
  {
    topic: 'sante', article: 'LPD art. 5 let. c ch. 2', label: 'health',
    terms: [
      'maladie', 'malade', 'état de santé', 'problème de santé', 'problèmes de santé', 'raisons de santé', 'santé fragile', 'congé maladie',
      'arrêt maladie', 'certificat médical', 'incapacité de travail', 'handicapé', 'handicapée', 'invalide', 'invalidité', 'rente AI', 'rente-AI',
      'burn-out', 'burnout', 'dépression', 'dépressif', 'dépressive', 'cancer', 'diagnostic', 'diagnostiqué', 'diagnostiquée', 'hospitalisé',
      'hospitalisée', 'hospitalisation', 'traitement médical', 'grossesse', 'enceinte', 'séropositif', 'séropositive', 'VIH', 'sida',
      'toxicomane', 'alcoolique', 'dossier médical',
      'Krankheit', 'krank', 'krankgeschrieben', 'Krankschreibung', 'Gesundheitszustand', 'gesundheitliche Gründe', 'Arbeitsunfähigkeit',
      'arbeitsunfähig', 'behindert', 'invalid', 'Invalidität', 'IV-Rente', 'Burnout', 'Depression', 'depressiv', 'Krebs', 'Diagnose', 'diagnostiziert',
      'hospitalisiert', 'Spitalaufenthalt', 'schwanger', 'Schwangerschaft', 'HIV', 'Aids', 'Arztzeugnis', 'Krankengeschichte',
    ],
  },
  {
    topic: 'intime', article: 'LPD art. 5 let. c ch. 2', label: 'intimate sphere',
    terms: [
      'orientation sexuelle', 'vie sexuelle', 'sexualité', 'homosexuel', 'homosexuelle', 'lesbienne', 'bisexuel', 'bisexuelle', 'transgenre',
      'sexuelle Orientierung', 'Sexualleben', 'Sexualität', 'homosexuell', 'schwul', 'lesbisch', 'bisexuell', 'transgender', 'Intimsphäre',
    ],
  },
  {
    topic: 'origine', article: 'LPD art. 5 let. c ch. 2', label: 'racial or ethnic origin, nationality',
    terms: [
      'origine ethnique', 'ethnie', 'ethnique', 'race', 'racial', 'raciale', 'couleur de peau', 'nationalité', 'ressortissant', 'ressortissante',
      'permis B', 'permis C', 'permis F', 'permis N', 'permis L', 'permis S', 'permis de séjour', 'requérant d’asile', 'requérante d’asile', 'asile',
      'réfugié', 'réfugiée', 'sans-papiers', 'naturalisé', 'naturalisée', 'naturalisation', 'origine raciale',
      'ethnische Herkunft', 'Ethnie', 'ethnisch', 'Rasse', 'Hautfarbe', 'Nationalität', 'Staatsangehörigkeit', 'Staatsbürgerschaft', 'Ausländer',
      'Ausländerin', 'Asyl', 'asylsuchend', 'Asylsuchende', 'Flüchtling', 'Aufenthaltsbewilligung', 'Niederlassungsbewilligung', 'eingebürgert',
      'Einbürgerung', 'Sans-Papiers',
    ],
  },
  {
    topic: 'genetique', article: 'LPD art. 5 let. c ch. 3', label: 'genetic data',
    terms: ['génétique', 'ADN', 'test génétique', 'genetisch', 'DNA', 'Gentest'],
  },
  {
    topic: 'biometrique', article: 'LPD art. 5 let. c ch. 4', label: 'biometric data',
    terms: ['biométrique', 'empreinte digitale', 'empreintes digitales', 'reconnaissance faciale', 'biometrisch', 'Fingerabdruck', 'Gesichtserkennung'],
  },
  {
    topic: 'penal', article: 'LPD art. 5 let. c ch. 5', label: 'criminal or administrative proceedings or sanctions',
    terms: [
      'casier judiciaire', 'casier', 'condamné', 'condamnée', 'condamnation', 'poursuite pénale', 'poursuites pénales', 'procédure pénale',
      'plainte pénale', 'infraction', 'délit', 'crime', 'prison', 'détention', 'détenu', 'détenue', 'incarcéré', 'incarcérée', 'sanction administrative',
      'faillite personnelle', 'acte de défaut de biens', 'office des poursuites',
      'Strafregister', 'Strafregisterauszug', 'Vorstrafe', 'vorbestraft', 'verurteilt', 'Verurteilung', 'Strafverfahren', 'Strafanzeige',
      'Gefängnis', 'Haft', 'inhaftiert', 'Straftat', 'Delikt', 'Betreibung', 'Verlustschein', 'Privatkonkurs',
    ],
  },
  {
    topic: 'aide_sociale', article: 'LPD art. 5 let. c ch. 6', label: 'social-assistance measures',
    terms: [
      'aide sociale', 'bénéficiaire de l’aide sociale', 'revenu d’insertion', 'assistance sociale', 'prestations complémentaires', 'sous curatelle',
      'sous tutelle', 'curatelle', 'mesure de protection', 'endetté', 'endettée', 'surendetté', 'surendettée', 'surendettement', 'saisie sur salaire',
      'Sozialhilfe', 'Sozialhilfeempfänger', 'Sozialhilfeempfängerin', 'Sozialhilfebezug', 'Fürsorge', 'Ergänzungsleistungen', 'Beistandschaft',
      'verbeiständet', 'verschuldet', 'überschuldet', 'Lohnpfändung',
    ],
  },
];

export interface ExcludedTopicMatch {
  topic: ExcludedTopic['topic'];
  article: string;
  label: string;
  /** The term as the list spells it. */
  term: string;
}

const foldText = (s: string): string => foldAccents(String(s ?? '').toLowerCase()).replace(/[’'`´\-]/g, ' ').replace(/\s+/g, ' ');
const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// One regex per topic, longest term first so «parti socialiste» is reported before «socialiste».
const TOPIC_MATCHERS: ReadonlyArray<{ topic: ExcludedTopic; re: RegExp; terms: string[] }> = EXCLUDED_PERSON_TOPICS.map((topic) => {
  const terms = [...topic.terms].sort((a, b) => b.length - a.length);
  const alternation = terms.map((t) => escapeRe(foldText(t)).replace(/ /g, '\\s+')).join('|');
  return { topic, re: new RegExp(`(?<![a-z0-9])(?:${alternation})(?![a-z0-9])`, 'i'), terms };
});

/**
 * Does a free key, a value or a quote name a sensitive topic (§5.5)? The first topic hit, with
 * the article and the term, or null. «directrice du service des finances» passes; «membre du
 * syndicat», «Krankheit», «aide sociale», «parti socialiste» do not.
 */
export function matchesExcludedTopic(text: string | null | undefined): ExcludedTopicMatch | null {
  const folded = foldText(text ?? '');
  if (folded.trim() === '') return null;
  for (const { topic, re, terms } of TOPIC_MATCHERS) {
    const m = re.exec(folded);
    if (!m) continue;
    const hit = foldText(m[0]).replace(/\s+/g, ' ');
    const term = terms.find((t) => foldText(t) === hit) ?? m[0];
    return { topic: topic.topic, article: topic.article, label: topic.label, term };
  }
  return null;
}

// ── the wire types in `shared/types.ts` must spell these unions exactly ─────
//
// `types.ts` carries no imports (its header rule), so it writes the cell type, the kind and the
// state out by hand. These assertions fail to compile the moment one side drifts.
type Same<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;
const _kinds: Same<PersonCellKind, PersonProfileCellKind> = true;
const _types: Same<RasterCellType, PersonProfileCellType> = true;
const _coverage: Same<PersonCoverage, PersonProfileCoverage> = true;
const _states: Same<PersonCellState, 'held' | 'pending' | 'open' | 'not_applicable' | 'not_found' | 'computed'> = true;
void _kinds; void _types; void _coverage; void _states;
